// Pure helpers for normalizing, formatting, and validating routing identifier
// tokens (NPIs and fax numbers). Shared by the external partner signup form
// and the portal Routing Identifiers settings tab.

export const normalizeDigits = (value) => value.toString().replace(/\D/g, '');

export const formatWithPattern = (digits, groups) => {
  let cursor = 0;
  const parts = [];

  groups.forEach((groupSize) => {
    if (cursor >= digits.length) return;
    const next = digits.slice(cursor, cursor + groupSize);
    if (next.length > 0) parts.push(next);
    cursor += groupSize;
  });

  return parts.join('-');
};

export const normalizeNpiToken = (value) => normalizeDigits(value).slice(0, 10);

export const isValidNpiToken = (token) => token.length === 10;

export const normalizeFaxToken = (value) => {
  const compact = value.toString().replace(/\s/g, '');
  if (compact.length === 0) return '';

  const hasPlusPrefix = compact.startsWith('+');
  const digits = compact.replace(/\D/g, '');

  if (hasPlusPrefix) {
    if (!digits.startsWith('1')) return '+';
    return `+${digits.slice(0, 11)}`;
  }

  if (digits.startsWith('1')) return digits.slice(0, 11);
  return digits.slice(0, 10);
};

export const formatFaxToken = (value) => {
  if (!value) return '';

  if (value.startsWith('+')) {
    if (!value.startsWith('+1')) return '+';
    const countryPayload = value.slice(2).replace(/\D/g, '');
    const formattedPayload = formatWithPattern(countryPayload, [3, 3, 4]);
    return formattedPayload.length > 0 ? `+1-${formattedPayload}` : '+1';
  }

  const digits = normalizeDigits(value);
  if (digits.startsWith('1')) {
    const localPayload = formatWithPattern(digits.slice(1), [3, 3, 4]);
    return localPayload.length > 0 ? `1-${localPayload}` : '1';
  }

  return formatWithPattern(digits, [3, 3, 4]);
};

export const isValidFaxToken = (token) => {
  if (!token) return false;
  if (token.startsWith('+1')) return token.length === 12;
  if (token.startsWith('1')) return token.length === 11;
  return token.length === 10;
};

// Reduce a fax (or NPI) value to its local digit key, dropping a leading US
// country code, so duplicate detection, filtering, and display all agree on
// the same digits regardless of formatting or +1.
export const faxLocalDigits = (value) => {
  const digits = normalizeDigits(value || '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

// Display a stored fax value as (555) 123-4567 for readability. Falls back to
// the raw value when it isn't a 10/11-digit US number.
export const formatFaxDisplay = (value) => {
  const local = faxLocalDigits(value);
  if (local.length !== 10) return value;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};
